import { Link } from "react-router-dom";
import { ArrowRight, Heart, MapPin, ShieldCheck, Sparkles } from "lucide-react";
import { useLanguage } from "../i18n";
import { useSEO } from "../seo";
import { IMAGES, LINKS } from "../data";

const COPY = {
  it: {
    title: "Chi siamo | OGStay — Ospitalità boutique in Ogliastra",
    desc: "OGStay è il team locale che porta gli standard dell'alta hôtellerie nelle case private dell'Ogliastra, in Sardegna. Per ospiti e proprietari.",
    kicker: "Chi siamo",
    h1: "L'alta hôtellerie,",
    h1i: "dentro le case dell'Ogliastra.",
    intro:
      "OGStay nasce in Ogliastra, sulla costa orientale della Sardegna, da un'idea semplice: una casa vacanza può offrire la libertà di un appartamento e la cura di un boutique hotel. Siamo un team locale che vive questo territorio ogni giorno e segue personalmente ogni struttura.",
    valuesTitle: "Come lavoriamo",
    values: [
      { icon: MapPin, h: "Presenza locale", p: "Siamo sul territorio, tutto l'anno. Ogni casa viene seguita da persone che conoscono l'Ogliastra e ci vivono." },
      { icon: Sparkles, h: "Standard hôtellerie", p: "Biancheria professionale, pulizie alberghiere, check-in curato e assistenza continua: portiamo in ogni casa gli standard dell'hotellerie di alto livello." },
      { icon: ShieldCheck, h: "Tutela del proprietario", p: "Poche proprietà, seguite bene. Selezioniamo le case con cui possiamo fare un ottimo lavoro e garantiamo trasparenza su numeri e gestione." },
      { icon: Heart, h: "Attenzione all'ospite", p: "Accoglienza personale, consigli autentici e un concierge sempre disponibile: l'ospite deve sentirsi atteso, non ospitato." },
    ],
    founderKicker: "Il fondatore",
    founderH: "Enrico",
    founderP1:
      "OGStay nasce dall'esperienza diretta di Enrico nell'ospitalità e nella gestione di case vacanza in Ogliastra. Dopo anni passati a vedere case splendide gestite senza metodo, ha deciso di costruire un modo diverso di lavorare: professionale, trasparente e radicato sul territorio.",
    founderP2:
      "Oggi coordina personalmente il team OGStay: revenue management, housekeeping, manutenzione e accoglienza lavorano come un unico sistema, così l'ospite vive una vacanza senza pensieri e il proprietario mantiene il controllo senza gestire tutto da solo.",
    methodKicker: "Il metodo",
    methodH: "Poche case, seguite bene.",
    methodP:
      "Non siamo una piattaforma automatica e non vogliamo esserlo. Ogni proprietà entra nel nostro sistema solo dopo un'analisi accurata, e ogni ospite viene seguito da persone reali. È il nostro modo di tutelare sia chi viaggia sia chi affida la propria casa.",
    ctaOwner: "Affida il tuo immobile",
    ctaStay: "Scopri le strutture",
  },
  en: {
    title: "About us | OGStay — Boutique hospitality in Ogliastra",
    desc: "OGStay is the local team bringing high-end hôtellerie standards to private homes in Ogliastra, Sardinia. For guests and owners.",
    kicker: "About us",
    h1: "High-end hôtellerie,",
    h1i: "inside the homes of Ogliastra.",
    intro:
      "OGStay was born in Ogliastra, on Sardinia's eastern coast, from a simple idea: a holiday home can offer the freedom of an apartment and the care of a boutique hotel. We are a local team who live this territory every day and personally look after every property.",
    valuesTitle: "How we work",
    values: [
      { icon: MapPin, h: "Local presence", p: "We are on the territory, all year round. Every home is followed by people who know Ogliastra and live here." },
      { icon: Sparkles, h: "Hôtellerie standards", p: "Professional linen, hotel-grade cleaning, curated check-in and continuous assistance: we bring high-end hospitality standards into every home." },
      { icon: ShieldCheck, h: "Owner protection", p: "Few properties, managed well. We select the homes where we can do an excellent job and guarantee transparency on numbers and management." },
      { icon: Heart, h: "Guest care", p: "Personal welcome, authentic tips and an always-available concierge: guests should feel expected, not just hosted." },
    ],
    founderKicker: "The founder",
    founderH: "Enrico",
    founderP1:
      "OGStay grew out of Enrico's direct experience in hospitality and holiday-home management in Ogliastra. After years of seeing beautiful homes managed without method, he decided to build a different way of working: professional, transparent and rooted in the territory.",
    founderP2:
      "Today he personally coordinates the OGStay team: revenue management, housekeeping, maintenance and guest care work as one system, so guests enjoy a worry-free holiday and owners keep control without managing everything themselves.",
    methodKicker: "The method",
    methodH: "Few homes, managed well.",
    methodP:
      "We are not an automated platform and we don't want to be. Every property joins our system only after a careful analysis, and every guest is followed by real people. It's our way of protecting both travellers and the owners who entrust us with their homes.",
    ctaOwner: "Entrust your property",
    ctaStay: "Explore our homes",
  },
};

export default function ChiSiamoPage() {
  const { lang } = useLanguage();
  const c = COPY[lang];
  useSEO(c.title, c.desc);

  return (
    <div data-testid="about-page" className="bg-warm">
      <section className="px-5 pb-20 pt-36 sm:px-8 sm:pt-44">
        <div className="mx-auto max-w-4xl">
          <p className="kicker text-navy" data-testid="about-kicker">
            <span className="h-px w-8 bg-sky" />
            {c.kicker}
          </p>
          <h1 data-testid="about-title" className="mt-5 font-display text-4xl font-semibold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            {c.h1}
            <span className="block italic text-med">{c.h1i}</span>
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-mute sm:text-lg">{c.intro}</p>
        </div>
      </section>

      <section className="px-5 pb-24 sm:px-8">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-[2rem]">
          <img
            src={IMAGES.landscape}
            alt={lang === "en" ? "Ogliastra coast, Sardinia" : "Costa dell'Ogliastra, Sardegna"}
            loading="lazy"
            className="h-64 w-full object-cover sm:h-96"
            data-testid="about-image"
          />
        </div>
      </section>

      <section className="bg-white px-5 py-24 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-testid="about-values-title">
            {c.valuesTitle}
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {c.values.map((v, i) => (
              <div key={v.h} data-testid={`about-value-${i}`} className="rounded-[1.75rem] bg-warm p-8 ring-1 ring-navy/10">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-sky">
                  <v.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold text-navy">{v.h}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mute">{v.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy px-5 py-24 text-white sm:px-8" data-testid="about-founder">
        <div className="mx-auto max-w-4xl">
          <p className="kicker text-sky">
            <span className="h-px w-8 bg-sky" />
            {c.founderKicker}
          </p>
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{c.founderH}</h2>
          <p className="mt-6 text-base leading-relaxed text-white/70 sm:text-lg">{c.founderP1}</p>
          <p className="mt-4 text-base leading-relaxed text-white/70 sm:text-lg">{c.founderP2}</p>
        </div>
      </section>

      <section className="px-5 py-24 sm:px-8">
        <div className="mx-auto max-w-4xl">
          <p className="kicker text-navy">
            <span className="h-px w-8 bg-sky" />
            {c.methodKicker}
          </p>
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">{c.methodH}</h2>
          <p className="mt-6 text-base leading-relaxed text-mute sm:text-lg">{c.methodP}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link data-testid="about-owner-cta" to="/servizi" className="btn-primary !px-7 !py-3.5 text-sm">
              {c.ctaOwner}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a data-testid="about-stays-cta" href={LINKS.booking} className="btn-secondary !px-7 !py-3.5 text-sm">
              {c.ctaStay}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
